"use client"

import type React from "react"
import { useState, useEffect, useRef, useCallback } from "react"
import ReactMarkdown from 'react-markdown'
import ClearHistoryButton from "./ClearHistoryButton"
import ContextSelectionButton from "./ContextSelectionButton"
import MarkdownEditorModal from "./MarkdownEditorModal"
import ChatMessageItem, { ReasoningStepView } from "./ChatMessageItem"
import type { Message, ReasoningStep, Source, DocumentDownload } from "./ChatMessageItem"
import { streamChat, ChatStreamHttpError } from '../api/chatStream'
import { useTranslation } from "react-i18next"
import { useAuth } from "../../../contexts/AuthContext"
import { useChatContext } from "../../../contexts/ChatContextProvider"
import { useAttachments } from "../../../contexts/AttachmentContext"
import type { AttachedDocument } from "../../../contexts/AttachmentContext"
import { getFreshAccessToken, getAccessToken, SessionExpiredError } from "../../../lib/api/api"
import { X, Loader2, FileText, Download, ChevronDown, Mic, Square, Bold, Italic, Strikethrough, Heading1, Heading2, List, ListOrdered, Quote, Code, Minus, Send } from "lucide-react"
import { insertMarkdown, insertLinePrefix, insertCodeBlock, insertHorizontalRule, applyMarkdownResult } from '../utils/markdownHelpers'
import OfficialSourceNotice from '../../../shared/components/OfficialSourceNotice'
import type { OfficialSource } from '../../../shared/components/OfficialSourceNotice'
import { loadDocumentView, attachmentDocType } from '../utils/documentViewer'
import type { DocumentView } from '../utils/documentViewer'
import { pickLocalVoice, speakWithBrowser } from '../utils/browserSpeech'
import { recordingFileName, transcriptionErrorText } from '../utils/dictation'
import toast from 'react-hot-toast'
import { useCapabilities } from '../../../shared/hooks/useCapabilities'

const STORAGE_KEY_PREFIX = 'chat_msgs'

/** The document viewer: the articles of an act (one highlighted), a text or a
 * file behind a blob: URL (revoked when the viewer closes or changes). */
interface DocViewerState extends DocumentView {
  title: string
  highlightArticleNumber?: string
}

interface OpenDocumentOptions {
  highlightArticleNumber?: string
  officialSource?: OfficialSource
  officialUrl?: string
  // Shown when the document itself can't be (the passage the answer used)
  fallbackText?: string
}

// Only same-origin URLs may be fetched with the bearer token.
const isSameOriginUrl = (u: string): boolean => {
  try {
    return new URL(u, window.location.origin).origin === window.location.origin
  } catch {
    return false
  }
}

export default function Chat() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { contextSettings, cantons } = useChatContext()
  const { attachedDocuments, detachDocument } = useAttachments()
  // Use opaque key instead of email for storage
  const storageKey = user ? `${STORAGE_KEY_PREFIX}_${btoa(user.email).slice(0, 12)}` : STORAGE_KEY_PREFIX

  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState("")
  const [isEditorModalOpen, setIsEditorModalOpen] = useState(false)
  const [isClearingHistory, setIsClearingHistory] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isInputExpanded, setIsInputExpanded] = useState(false)
  const [recordingSeconds, setRecordingSeconds] = useState(0)
  const [waveformBars, setWaveformBars] = useState<number[]>(new Array(24).fill(2))
  const analyserRef = useRef<AnalyserNode | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const animFrameRef = useRef<number | null>(null)
  const inputTextareaRef = useRef<HTMLTextAreaElement>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [elapsedTimer, setElapsedTimer] = useState(0)
  const [liveMetrics, setLiveMetrics] = useState<{tokPerSec: number, vramGb: number, tokens: number} | null>(null)
  const [currentReasoningSteps, setCurrentReasoningSteps] = useState<ReasoningStep[]>([])
  const [docViewer, setDocViewer] = useState<DocViewerState | null>(null)
  const [isDocLoading, setIsDocLoading] = useState(false)
  // Bumped by every document load and on unmount: a load that was superseded
  // drops its result (and revokes its blob: URL).
  const viewerRequestRef = useRef(0)

  const [playingMessageId, setPlayingMessageId] = useState<string | null>(null)
  const [ttsLoading, setTtsLoading] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  // Read-aloud: the server's `say` (the Mac) or a local voice of this browser (NADIA online)
  const { tts } = useCapabilities()
  const [browserVoice, setBrowserVoice] = useState<SpeechSynthesisVoice | null>(null)
  const stopSpeechRef = useRef<(() => void) | null>(null)
  const ttsAvailable = tts === 'server' || (tts === 'browser' && browserVoice !== null)
  // In-flight chat stream: aborted on stop, on a new message and on unmount.
  const abortControllerRef = useRef<AbortController | null>(null)
  // Live (non-batched) reasoning steps of the in-flight stream.
  const liveReasoningRef = useRef<ReasoningStep[]>([])
  // Pending requestAnimationFrame that flushes streamed updates to React.
  const streamRafRef = useRef<number | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const mediaStreamRef = useRef<MediaStream | null>(null)
  const hasMicSupport = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
  const hasRestoredRef = useRef(false)

  // Restore chat from sessionStorage on mount; if nothing to restore, clear
  // server-side history so a fresh session starts clean. Session-scoped: survives
  // intra-tab navigation, resets on tab close.
  useEffect(() => {
    const stored = sessionStorage.getItem(storageKey)
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as Message[]
        setMessages(parsed)
        hasRestoredRef.current = true
        return
      } catch {}
    }
    setMessages([])
    setCurrentReasoningSteps([])
    const token = getAccessToken()
    if (token) {
      fetch('/api/v1/chat-stream/clear-history', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      }).catch(() => {})
    }
    hasRestoredRef.current = true
  }, [storageKey])

  // Auto-scroll to bottom on new messages or reasoning. While streaming, jump
  // instead of smooth-scrolling: a smooth animation per streamed chunk piles up
  // and makes the page stutter.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: isLoading ? 'auto' : 'smooth' })
  }, [messages, currentReasoningSteps, isLoading])

  // Recording timer
  useEffect(() => {
    if (!isRecording) return
    const interval = setInterval(() => setRecordingSeconds(s => s + 1), 1000)
    return () => clearInterval(interval)
  }, [isRecording])

  // Waveform animation
  useEffect(() => {
    if (!isRecording || !analyserRef.current) return
    const analyser = analyserRef.current
    const bufferLength = analyser.frequencyBinCount
    const dataArray = new Uint8Array(bufferLength)

    const updateBars = () => {
      analyser.getByteFrequencyData(dataArray)
      // Sample 24 bars from the frequency data
      const bars: number[] = []
      const step = Math.max(1, Math.floor(bufferLength / 24))
      for (let i = 0; i < 24; i++) {
        const val = dataArray[Math.min(i * step, bufferLength - 1)]
        bars.push(Math.max(3, Math.round((val / 255) * 44)))
      }
      setWaveformBars(bars)
      animFrameRef.current = requestAnimationFrame(updateBars)
    }
    animFrameRef.current = requestAnimationFrame(updateBars)

    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current)
    }
  }, [isRecording])

  // Live elapsed timer during loading
  useEffect(() => {
    if (!isLoading) return
    setElapsedTimer(0)
    const interval = setInterval(() => setElapsedTimer(t => t + 1), 1000)
    return () => clearInterval(interval)
  }, [isLoading])

  // Save messages to sessionStorage whenever they change. Gated on the restore
  // having run, otherwise the first render would clobber a pending restore with "[]".
  useEffect(() => {
    if (!hasRestoredRef.current) return
    sessionStorage.setItem(storageKey, JSON.stringify(messages))
  }, [messages, storageKey])

  // Cleanup on unmount: abort the in-flight stream and any pending flush,
  // drop the document loads still in progress
  useEffect(() => {
    return () => {
      viewerRequestRef.current += 1
      abortControllerRef.current?.abort()
      abortControllerRef.current = null
      if (streamRafRef.current != null) {
        cancelAnimationFrame(streamRafRef.current)
        streamRafRef.current = null
      }
    }
  }, [])

  // Cleanup TTS audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
    }
  }, [])

  // Cleanup ASR on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop()
      }
      mediaStreamRef.current?.getTracks().forEach(t => t.stop())
    }
  }, [])

  const transcribeAudio = async (blob: Blob, fileName: string) => {
    setIsTranscribing(true)
    try {
      const token = await getFreshAccessToken()
      if (!token) return
      const formData = new FormData()
      formData.append('file', blob, fileName)
      const resp = await fetch('/api/v1/chat-stream/transcribe', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      })
      if (resp.ok) {
        const data = await resp.json()
        if (data.text) {
          const text = data.text.trim()
          setInput(text)
          // Auto-submit after transcription
          setTimeout(() => {
            const form = document.querySelector('form')
            if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))
          }, 100)
        }
      } else {
        toast.error(await transcriptionErrorText(resp, t('chat.input.transcriptionFailed')))
      }
    } catch (err) {
      console.error('Transcription error:', err)
      toast.error(t('chat.input.transcriptionFailed'))
    } finally {
      setIsTranscribing(false)
    }
  }

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      mediaStreamRef.current = stream

      // Setup audio analyser for waveform
      const audioContext = new AudioContext()
      const source = audioContext.createMediaStreamSource(stream)
      const analyser = audioContext.createAnalyser()
      analyser.fftSize = 64
      source.connect(analyser)
      audioContextRef.current = audioContext
      analyserRef.current = analyser

      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : ''
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
      audioChunksRef.current = []
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data)
      }
      recorder.onstop = () => {
        const type = recorder.mimeType || mimeType || 'audio/webm'
        const blob = new Blob(audioChunksRef.current, { type })
        transcribeAudio(blob, recordingFileName(type))
      }
      recorder.start()
      mediaRecorderRef.current = recorder
      setIsRecording(true)
      setRecordingSeconds(0)
    } catch (err) {
      console.error('Microphone access denied:', err)
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
    mediaStreamRef.current?.getTracks().forEach(t => t.stop())
    mediaRecorderRef.current = null
    // Cleanup audio analyser
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current)
    audioContextRef.current?.close()
    analyserRef.current = null
    audioContextRef.current = null
    mediaStreamRef.current = null
    setIsRecording(false)
  }

  // Cleanup blob URL when modal closes
  useEffect(() => {
    return () => {
      if (docViewer?.blobUrl) URL.revokeObjectURL(docViewer.blobUrl)
    }
  }, [docViewer])

  // Auto-scroll to highlighted article when modal opens with articles
  useEffect(() => {
    if (docViewer?.articles && docViewer.highlightArticleNumber) {
      const timer = setTimeout(() => {
        const el = document.getElementById('highlighted-article')
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [docViewer?.articles, docViewer?.highlightArticleNumber])

  useEffect(() => {
    if (tts !== 'browser' || typeof window === 'undefined' || !('speechSynthesis' in window)) return
    const synth = window.speechSynthesis
    const update = () => setBrowserVoice(pickLocalVoice(synth.getVoices(), 'it'))
    update()
    synth.addEventListener('voiceschanged', update)
    return () => {
      synth.removeEventListener('voiceschanged', update)
      stopSpeechRef.current?.()
    }
  }, [tts])

  const handleTTS = async (messageId: string, text: string) => {
    if (tts === 'browser') {
      const wasPlaying = playingMessageId === messageId
      stopSpeechRef.current?.()
      stopSpeechRef.current = null
      if (wasPlaying || !browserVoice) return
      setTtsLoading(false)
      setPlayingMessageId(messageId)
      stopSpeechRef.current = speakWithBrowser(text, browserVoice, () => {
        setPlayingMessageId(current => (current === messageId ? null : current))
      })
      return
    }
    // If already playing this message, stop it
    if (playingMessageId === messageId) {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
      setPlayingMessageId(null)
      return
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current = null
    }

    setPlayingMessageId(messageId)
    setTtsLoading(true)

    try {
      const token = await getFreshAccessToken()
      if (!token) return

      const resp = await fetch('/api/v1/chat-stream/text-to-speech', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text })
      })

      if (!resp.ok) throw new Error('TTS request failed')

      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const audio = new Audio(url)
      audioRef.current = audio

      audio.onended = () => {
        URL.revokeObjectURL(url)
        audioRef.current = null
        setPlayingMessageId(null)
      }

      setTtsLoading(false)
      audio.play()
    } catch (err) {
      console.error('TTS error:', err)
      setPlayingMessageId(null)
      setTtsLoading(false)
    }
  }

  // Open a source or an attachment in the viewer (documentViewer.ts maps
  // each doc_type to its API; Bearer token only).
  const openDocument = async (
    docType: string | undefined,
    docId: string | undefined,
    title: string,
    options: OpenDocumentOptions = {},
  ) => {
    const request = viewerRequestRef.current + 1
    viewerRequestRef.current = request
    setIsDocLoading(true)
    let view: DocumentView | null = null
    let failed = false
    try {
      view = await loadDocumentView(docType, docId)
    } catch (err) {
      console.error('Error opening document:', err)
      failed = true
    }
    if (request !== viewerRequestRef.current) {
      // Superseded or unmounted meanwhile
      if (view?.blobUrl) URL.revokeObjectURL(view.blobUrl)
      return
    }
    setIsDocLoading(false)
    if (view) {
      setDocViewer({
        title,
        highlightArticleNumber: options.highlightArticleNumber,
        officialSource: options.officialSource,
        officialUrl: options.officialUrl,
        ...view,
      })
    } else {
      setDocViewer({
        title,
        text: options.fallbackText || t(failed ? 'chat.viewer.error' : 'chat.viewer.unavailable'),
      })
    }
  }

  // The blob: URL is revoked by the effect above
  const closeDocViewer = () => setDocViewer(null)

  const handleSourceClick = (source: Source) => {
    const isArticle = source.doc_type === 'law_article'
    openDocument(source.doc_type, source.doc_id, isArticle ? (source.doc_title || source.title) : source.title, {
      highlightArticleNumber: isArticle ? source.article_number || undefined : undefined,
      officialSource: source.official_source,
      officialUrl: source.official_url,
      fallbackText: source.content,
    })
  }

  const handleAttachmentClick = (doc: AttachedDocument) => {
    openDocument(attachmentDocType(doc.type), doc.id, doc.title)
  }

  // Stable callbacks for the memoized message list: they forward to the latest
  // handler through a ref, so past messages don't re-render on every keystroke
  // or streamed chunk.
  const handlersRef = useRef({ handleTTS, handleSourceClick })
  handlersRef.current = { handleTTS, handleSourceClick }
  const onTTS = useCallback((messageId: string, text: string) => {
    handlersRef.current.handleTTS(messageId, text)
  }, [])
  const onSourceClick = useCallback((source: Source) => {
    handlersRef.current.handleSourceClick(source)
  }, [])
  const onDownloadDocument = useCallback(async (download: DocumentDownload) => {
    if (!isSameOriginUrl(download.download_url)) return
    const token = await getFreshAccessToken()
    if (!token) return
    const resp = await fetch(download.download_url, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
    if (resp.ok) {
      const blob = await resp.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = download.filename
      a.click()
      URL.revokeObjectURL(url)
    }
  }, [])

  const cancelStreamFlush = () => {
    if (streamRafRef.current != null) {
      cancelAnimationFrame(streamRafRef.current)
      streamRafRef.current = null
    }
  }

  const handleSendMessage = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const messageText = input
    if (!messageText.trim() || isLoading) return

    // Cancel any previous stream BEFORE starting a new one
    abortControllerRef.current?.abort()
    cancelStreamFlush()
    const controller = new AbortController()
    abortControllerRef.current = controller
    // False once this stream was stopped, replaced by a newer one, finished or unmounted
    const isCurrent = () => abortControllerRef.current === controller && !controller.signal.aborted

    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      content: messageText,
      role: 'user'
    }
    setMessages(prev => [...prev, userMessage])
    setIsLoading(true)
    setLiveMetrics(null)
    setCurrentReasoningSteps([])
    setInput('')

    const startTime = Date.now()
    const reasoningSteps: ReasoningStep[] = []
    liveReasoningRef.current = reasoningSteps
    let finalResponse = ""
    let answerLanguage: string | undefined
    let sources: Source[] = []
    let documentDownload: DocumentDownload | undefined = undefined
    let pendingMetrics: { tokPerSec: number, vramGb: number, tokens: number } | null = null
    let streamError: string | null = null
    let finished = false

    // Batch streamed updates: at most one React render per animation frame
    // instead of one per token.
    const scheduleFlush = () => {
      if (streamRafRef.current != null) return
      streamRafRef.current = requestAnimationFrame(() => {
        streamRafRef.current = null
        if (!isCurrent()) return
        setCurrentReasoningSteps([...reasoningSteps])
        if (pendingMetrics) {
          setLiveMetrics(pendingMetrics)
          pendingMetrics = null
        }
      })
    }

    const finishWith = (message: Message) => {
      finished = true
      cancelStreamFlush()
      if (abortControllerRef.current === controller) abortControllerRef.current = null
      liveReasoningRef.current = []
      setMessages(prev => [...prev, message])
      setCurrentReasoningSteps([])
      setIsLoading(false)
    }

    const failWith = (content: string) => finishWith({
      id: (Date.now() + 1).toString(),
      content,
      role: 'assistant',
      reasoning: reasoningSteps.length > 0 ? [...reasoningSteps] : undefined,
    })

    // Handle one SSE frame; returns true to stop reading (after 'done').
    const handleEvent = (data: any): boolean => {
      if (!isCurrent()) return true

      switch (data?.type) {
        case 'reasoning': {
          const content = typeof data.content === 'string' ? data.content : String(data.content ?? '')
          if (data.step === 'thinking') {
            // Streaming thinking: append to last thinking step
            const last = reasoningSteps[reasoningSteps.length - 1]
            if (last && last.step === 'thinking') {
              // New object so the memoized step view re-renders
              reasoningSteps[reasoningSteps.length - 1] = { ...last, content: last.content + content }
            } else {
              reasoningSteps.push({ content, step: 'thinking', timestamp: new Date().toISOString() })
            }
          } else {
            // Regular reasoning step (search, analysis, etc.)
            reasoningSteps.push({ content, step: data.step, timestamp: new Date().toISOString() })
          }
          scheduleFlush()
          return false
        }
        case 'sources':
          sources = Array.isArray(data.content) ? data.content : []
          return false
        case 'document_ready':
          documentDownload = data.content
          return false
        case 'metrics':
          pendingMetrics = {
            tokPerSec: data.content?.tokens_per_second || 0,
            vramGb: data.content?.vram_gb || 0,
            tokens: data.content?.tokens_generated || 0,
          }
          scheduleFlush()
          return false
        case 'final_response':
          finalResponse = data.content
          answerLanguage = typeof data.answer_language === 'string' ? data.answer_language : undefined
          return false
        case 'error':
          // Backend error frame (usually followed by 'done'): show its text.
          streamError = typeof data.content === 'string' && data.content.trim()
            ? data.content
            : t('chat.messages.streamError')
          return false
        case 'done': {
          const elapsedSeconds = Math.round((Date.now() - startTime) / 1000)
          let content: string
          if (streamError && !finalResponse) {
            content = streamError
          } else if (streamError) {
            content = `${finalResponse}\n\n*${streamError}*`
          } else {
            content = finalResponse || t('chat.messages.emptyResponse')
          }
          finishWith({
            id: (Date.now() + 1).toString(),
            content,
            role: 'assistant',
            reasoning: reasoningSteps,
            sources: sources.length > 0 ? sources : undefined,
            documentDownload: documentDownload || undefined,
            elapsedSeconds,
            answerLanguage,
          })
          return true
        }
        default:
          return false
      }
    }

    // Conversation history (last 10 messages, each truncated to keep the prompt small)
    const conversationHistory = messages
      .slice(-10)
      .filter((msg): msg is Message & { role: 'user' | 'assistant' } => msg.role === 'user' || msg.role === 'assistant')
      .map(msg => ({ role: msg.role, content: msg.content.substring(0, 500) }))

    try {
      // buildChatBody (chatStream.ts) turns these into the server's snake_case body.
      await streamChat({
        message: messageText,
        settings: contextSettings,
        cantons,
        attachments: attachedDocuments.map(doc => ({ id: doc.id, type: doc.type })),
        history: conversationHistory,
      }, controller.signal, handleEvent)
      if (!finished && isCurrent()) {
        // The stream closed without a 'done' frame
        failWith(streamError || t('chat.messages.connectionInterrupted'))
      }
    } catch (error) {
      // Stopped by the user, replaced by a newer message or unmounted: nothing to show
      if (!isCurrent()) return

      if (error instanceof SessionExpiredError) {
        // The API layer already triggered the global session-expired flow.
        failWith(t('chat.messages.sessionExpired'))
      } else if (error instanceof ChatStreamHttpError) {
        const detail = error.message.length > 300 ? `${error.message.slice(0, 300)}…` : error.message
        failWith(t('chat.messages.httpError', { status: error.status, detail }))
      } else {
        console.error('Chat stream error:', error)
        failWith(streamError || t('chat.messages.connectionInterrupted'))
      }
    }
  }

  const handleStopGeneration = () => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    cancelStreamFlush()
    setIsLoading(false)
    // Add partial response if reasoning steps exist
    const steps = liveReasoningRef.current
    liveReasoningRef.current = []
    if (steps.length > 0) {
      const stoppedMsg: Message = {
        id: (Date.now() + 1).toString(),
        content: t('chat.messages.stopped'),
        role: 'assistant',
        reasoning: [...steps],
      }
      setMessages(prev => [...prev, stoppedMsg])
    }
    setCurrentReasoningSteps([])
  }

  const handleClearHistory = async () => {
    setIsClearingHistory(true)
    try {
      // Clear chat history on server
      const token = getAccessToken()
      if (token) {
        const response = await fetch('/api/v1/chat-stream/clear-history', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
        if (!response.ok) {
          console.error('Failed to clear chat history')
        }
      }

      // Clear local state
      setMessages([])
      setCurrentReasoningSteps([])
      sessionStorage.removeItem(storageKey)
    } catch (error) {
      console.error("Failed to clear chat history:", error)
    } finally {
      setIsClearingHistory(false)
    }
  }

  return (
    <div className="w-full">
      <div className="h-[calc(100vh-8rem)] overflow-hidden rounded-xl border border-gray-200 shadow-sm bg-white dark:bg-gray-900 dark:border-gray-800 flex flex-col">
        <div className="flex-1 overflow-y-auto p-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {/* Message List */}
          <div className="space-y-4">
            {messages.map((message) => (
              <ChatMessageItem
                key={message.id}
                message={message}
                ttsState={playingMessageId === message.id ? (ttsLoading ? 'loading' : 'playing') : 'idle'}
                ttsAvailable={ttsAvailable}
                isDocLoading={isDocLoading}
                onTTS={onTTS}
                onSourceClick={onSourceClick}
                onDownloadDocument={onDownloadDocument}
              />
            ))}

            {/* Current reasoning steps (while loading) */}
            {isLoading && currentReasoningSteps.length > 0 && (
              <details open className="mt-2 ml-8">
                <summary className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden">
                  <Loader2 className="animate-spin h-3 w-3" />
                  <span>{t('chat.status.processing')}</span>
                  <ChevronDown size={14} className="chevron-icon transition-transform duration-200" />
                </summary>
                <div className="mt-1 space-y-1">
                  {currentReasoningSteps.map((step, idx) => (
                    <ReasoningStepView key={idx} content={step.content} streaming />
                  ))}
                </div>
              </details>
            )}

            {/* Loading indicator + stop button */}
            {isLoading && (
              <div className="flex items-center gap-3 justify-start">
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="animate-spin h-4 w-4" />
                    <span>{t('chat.status.thinking')}</span>
                    <span className="text-xs text-gray-400">
                      {elapsedTimer < 60
                        ? t('chat.elapsed.seconds', { s: elapsedTimer })
                        : t('chat.elapsed.minutes', { m: Math.floor(elapsedTimer / 60), s: elapsedTimer % 60 })}
                    </span>
                  </div>
                  {liveMetrics && (
                    <div className="flex items-center gap-2 text-[10px] text-gray-400 ml-8">
                      <span>⚡ {t('chat.status.tokensPerSecond', { value: liveMetrics.tokPerSec })}</span>
                      <span>│</span>
                      <span>🧠 {t('chat.status.memory', { value: liveMetrics.vramGb })}</span>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleStopGeneration}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                  title={t('chat.status.stopTitle')}
                >
                  <Square size={12} fill="currentColor" />
                  <span>{t('chat.status.stop')}</span>
                </button>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Attached Documents Section */}
        {attachedDocuments.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
            <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">{t('chat.attachments.title')}</div>
            <div className="flex flex-wrap gap-2">
              {attachedDocuments.map(doc => (
                <div
                  key={`${doc.type}:${doc.id}`}
                  className="flex items-center gap-1 bg-white border border-gray-200 rounded-md px-3 py-1 text-sm dark:bg-gray-900 dark:border-gray-700"
                >
                  <button
                    type="button"
                    onClick={() => handleAttachmentClick(doc)}
                    disabled={isDocLoading}
                    className="text-gray-700 dark:text-gray-300 hover:underline text-left"
                    title={t('chat.attachments.open', { title: doc.title })}
                  >
                    {doc.title}
                  </button>
                  <button
                    type="button"
                    onClick={() => detachDocument(doc.id, doc.type)}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                    title={t('chat.attachments.remove')}
                    aria-label={t('chat.attachments.remove')}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input Section */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          {/* Controls row */}
          <div className="flex items-center gap-3 mb-3">
            <ClearHistoryButton
              onClear={handleClearHistory}
              disabled={isClearingHistory || messages.length === 0}
            />
            <ContextSelectionButton />
          </div>

          {/* Input form */}
          <form onSubmit={handleSendMessage} className="w-full">
            <div className={`relative w-full bg-white rounded-xl border shadow-sm dark:bg-gray-900 transition-all duration-200 ${
              isInputExpanded ? 'border-primary-300 dark:border-primary-600 ring-1 ring-primary-200 dark:ring-primary-700' : 'border-gray-200 dark:border-gray-700'
            }`}>
              {/* Toolbar - visible when expanded */}
              {isInputExpanded && (
                <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 dark:border-gray-700 flex-wrap">
                  {[
                    { icon: <Bold size={14} />, title: t('chat.toolbar.bold'), action: () => { if (inputTextareaRef.current) { const r = insertMarkdown(inputTextareaRef.current, '**', '**', t('chat.toolbar.placeholder')); applyMarkdownResult(inputTextareaRef.current, r, setInput) } } },
                    { icon: <Italic size={14} />, title: t('chat.toolbar.italic'), action: () => { if (inputTextareaRef.current) { const r = insertMarkdown(inputTextareaRef.current, '*', '*', t('chat.toolbar.placeholder')); applyMarkdownResult(inputTextareaRef.current, r, setInput) } } },
                    { icon: <Strikethrough size={14} />, title: t('chat.toolbar.strikethrough'), action: () => { if (inputTextareaRef.current) { const r = insertMarkdown(inputTextareaRef.current, '~~', '~~', t('chat.toolbar.placeholder')); applyMarkdownResult(inputTextareaRef.current, r, setInput) } } },
                    null,
                    { icon: <Heading1 size={14} />, title: t('chat.toolbar.heading1'), action: () => { if (inputTextareaRef.current) { const r = insertLinePrefix(inputTextareaRef.current, '# '); applyMarkdownResult(inputTextareaRef.current, r, setInput) } } },
                    { icon: <Heading2 size={14} />, title: t('chat.toolbar.heading2'), action: () => { if (inputTextareaRef.current) { const r = insertLinePrefix(inputTextareaRef.current, '## '); applyMarkdownResult(inputTextareaRef.current, r, setInput) } } },
                    null,
                    { icon: <List size={14} />, title: t('chat.toolbar.bulletList'), action: () => { if (inputTextareaRef.current) { const r = insertLinePrefix(inputTextareaRef.current, '- '); applyMarkdownResult(inputTextareaRef.current, r, setInput) } } },
                    { icon: <ListOrdered size={14} />, title: t('chat.toolbar.numberedList'), action: () => { if (inputTextareaRef.current) { const r = insertLinePrefix(inputTextareaRef.current, '1. '); applyMarkdownResult(inputTextareaRef.current, r, setInput) } } },
                    { icon: <Quote size={14} />, title: t('chat.toolbar.quote'), action: () => { if (inputTextareaRef.current) { const r = insertLinePrefix(inputTextareaRef.current, '> '); applyMarkdownResult(inputTextareaRef.current, r, setInput) } } },
                    { icon: <Code size={14} />, title: t('chat.toolbar.code'), action: () => { if (inputTextareaRef.current) { const r = insertCodeBlock(inputTextareaRef.current); applyMarkdownResult(inputTextareaRef.current, r, setInput) } } },
                    { icon: <Minus size={14} />, title: t('chat.toolbar.divider'), action: () => { if (inputTextareaRef.current) { const r = insertHorizontalRule(inputTextareaRef.current); applyMarkdownResult(inputTextareaRef.current, r, setInput) } } },
                  ].map((btn, i) =>
                    btn === null ? (
                      <div key={`sep-${i}`} className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-0.5" />
                    ) : (
                      <button
                        key={i}
                        type="button"
                        onClick={btn.action}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                        title={btn.title}
                      >
                        {btn.icon}
                      </button>
                    )
                  )}
                </div>
              )}

              {/* Textarea or Recording UI */}
              <div className="flex items-end p-3 gap-2">
                {isRecording ? (
                  /* Recording waveform UI */
                  <div className="flex-1 flex items-center gap-3 min-h-[40px]">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                      <span className="text-sm text-red-500 font-medium">
                        {Math.floor(recordingSeconds / 60)}:{(recordingSeconds % 60).toString().padStart(2, '0')}
                      </span>
                    </div>
                    <div className="flex-1 flex items-center justify-center gap-[2px] h-12">
                      {waveformBars.map((height, i) => (
                        <div
                          key={i}
                          className="w-1.5 bg-red-400 dark:bg-red-500 rounded-full transition-all duration-75"
                          style={{ height: `${height}px` }}
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  /* Normal textarea */
                  <textarea
                    ref={inputTextareaRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onFocus={() => setIsInputExpanded(true)}
                    onBlur={(e) => {
                      if (e.relatedTarget?.closest?.('.toolbar-area')) return
                      if (!input.trim()) setIsInputExpanded(false)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        if (input.trim() && !isLoading) {
                          const form = e.currentTarget.closest('form')
                          if (form) form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))
                        }
                      }
                    }}
                    placeholder={t('chat.input.placeholder')}
                    disabled={isLoading}
                    className={`flex-1 resize-none bg-transparent outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400 transition-all duration-200 ${
                      isInputExpanded ? 'min-h-[100px]' : 'min-h-[24px] max-h-[24px]'
                    }`}
                    rows={isInputExpanded ? 4 : 1}
                  />
                )}

                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Mic / Stop button */}
                  {hasMicSupport && (
                    isRecording ? (
                      <button
                        type="button"
                        onClick={stopRecording}
                        className="p-2 bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
                        title={t('chat.input.stopRecording')}
                      >
                        <Square size={16} fill="white" className="text-white" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={startRecording}
                        disabled={isTranscribing || isLoading}
                        className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                          isTranscribing
                            ? 'text-gray-400'
                            : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                        }`}
                        title={isTranscribing ? t('chat.input.transcribing') : t('chat.input.dictate')}
                      >
                        {isTranscribing ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <Mic size={18} />
                        )}
                      </button>
                    )
                  )}

                  {/* Submit button - hidden during recording */}
                  {!isRecording && (
                    <button
                      type="submit"
                      className="p-2 bg-accent rounded-lg hover:bg-accent-dark transition-colors disabled:opacity-50"
                      disabled={isLoading || !input.trim()}
                      title={t('chat.input.send')}
                    >
                      <Send size={18} className="text-white" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Document Viewer Modal */}
      {docViewer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={closeDocViewer}>
          <div
            className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-[90vw] h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
              <div className="flex items-center gap-2 min-w-0">
                <FileText size={18} className="text-primary-600 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{docViewer.title}</span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {docViewer.blobUrl && (
                  <a
                    href={docViewer.blobUrl}
                    download={docViewer.title}
                    className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                    title={t('common.download')}
                  >
                    <Download size={18} />
                  </a>
                )}
                <button
                  onClick={closeDocViewer}
                  className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                  title={t('common.close')}
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            {docViewer.officialSource && (
              <OfficialSourceNotice
                className="px-4 py-2 border-b border-gray-200 dark:border-gray-700"
                sources={[docViewer.officialSource]}
                url={docViewer.officialUrl}
              />
            )}
            {/* Content */}
            <div className="flex-1 overflow-auto" id="doc-viewer-scroll-container">
              {docViewer.articles ? (
                <div className="p-6 max-w-3xl mx-auto space-y-4">
                  {docViewer.articles.map((article, idx) => {
                    const isHighlighted = docViewer.highlightArticleNumber &&
                      article.article_number === docViewer.highlightArticleNumber
                    return (
                      <div
                        key={idx}
                        id={isHighlighted ? 'highlighted-article' : undefined}
                        className={`rounded-lg p-4 ${
                          isHighlighted
                            ? 'bg-yellow-50 border-l-4 border-yellow-400 dark:bg-yellow-900/20 dark:border-yellow-500'
                            : 'bg-white border border-gray-100 dark:bg-gray-800 dark:border-gray-700'
                        }`}
                      >
                        <div className="flex items-baseline gap-2 mb-2">
                          <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                            {article.article_number}
                          </span>
                          {article.subtitle && (
                            <span className="text-sm text-gray-600 dark:text-gray-400 italic">
                              {article.subtitle}
                            </span>
                          )}
                        </div>
                        {article.section_title && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                            {article.section_title}
                          </div>
                        )}
                        <ReactMarkdown
                          className="prose prose-sm max-w-none text-gray-800 dark:text-gray-200"
                          components={{
                            p: ({ children }) => <p className="my-1 leading-relaxed">{children}</p>,
                            ul: ({ children }) => <ul className="list-disc list-inside my-1">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal list-inside my-1">{children}</ol>,
                            li: ({ children }) => <li className="ml-2">{children}</li>,
                          }}
                        >
                          {article.content}
                        </ReactMarkdown>
                      </div>
                    )
                  })}
                </div>
              ) : docViewer.text ? (
                <div className="p-6 max-w-3xl mx-auto">
                  <ReactMarkdown
                    className="prose prose-sm max-w-none text-gray-800 dark:text-gray-200"
                    components={{
                      p: ({ children }) => <p className="my-2 leading-relaxed">{children}</p>,
                      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                      ul: ({ children }) => <ul className="list-disc list-inside my-2">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal list-inside my-2">{children}</ol>,
                      li: ({ children }) => <li className="ml-2">{children}</li>,
                    }}
                  >
                    {docViewer.text}
                  </ReactMarkdown>
                </div>
              ) : docViewer.blobUrl && docViewer.contentType?.includes('pdf') ? (
                <iframe
                  src={docViewer.blobUrl}
                  className="w-full h-full border-0"
                  title={docViewer.title}
                />
              ) : docViewer.blobUrl && docViewer.contentType?.includes('image') ? (
                <div className="flex items-center justify-center h-full p-4">
                  <img src={docViewer.blobUrl} alt={docViewer.title} className="max-w-full max-h-full object-contain" />
                </div>
              ) : docViewer.blobUrl ? (
                // Other content (text, HTML, …) from the backend: fully
                // sandboxed so an HTML document can't run scripts with the
                // app's origin (blob: URLs inherit it). PDFs never get here
                // (handled above; sniffed in documentViewer.ts).
                <iframe
                  src={docViewer.blobUrl}
                  sandbox=""
                  className="w-full h-full border-0"
                  title={docViewer.title}
                />
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay for document fetch */}
      {isDocLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white dark:bg-gray-800 rounded-lg px-6 py-4 flex items-center gap-3 shadow-lg">
            <Loader2 className="animate-spin h-5 w-5 text-primary-600" />
            <span className="text-sm text-gray-700 dark:text-gray-300">{t('chat.viewer.loading')}</span>
          </div>
        </div>
      )}

      {/* Markdown Editor Modal */}
      <MarkdownEditorModal
        isOpen={isEditorModalOpen}
        onClose={() => setIsEditorModalOpen(false)}
        onSubmit={(content) => {
          setInput(content)
          setIsEditorModalOpen(false)
          // Auto-submit after setting input
          setTimeout(() => {
            const form = document.querySelector('form')
            if (form) {
              form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))
            }
          }, 0)
        }}
        initialValue={input}
        isLoading={isLoading}
      />
    </div>
  )
}
